/**
 * 登录状态 —— passkey 登录(2026-08-27 起), 外加一条应急后门。
 *
 * 三种进法, 优先级从高到低:
 *   ① **passkey**(日常): Touch ID 一按。私钥在 Secure Enclave, 服务端只有公钥。
 *   ② **注册**: 拿一个一次性注册码(要在私有网里 curl 才拿得到)换一把 passkey。
 *   ③ **应急后门 token**: 手工粘贴。它**只在私有网口认**, 公网口拿它也是 401 ——
 *      留着仅为 passkey 失效时救场(人换了电脑而 iCloud 钥匙串没同步)。
 *
 * ★ 无论哪条路, 登进来之后拿到的都是**同一种东西**: 一把存在 localStorage 的
 *   session token, 由 lib/hubClient.ts 的 api() 统一塞进 Authorization 头。
 *   所以数据层(useHubData / useWhep / /ws 子协议)对这次改动**一无所知, 也不需要知道**。
 *
 * 令牌有效性由 hub 说了算:本地存了一把不代表还能用(可能被吊销了)。
 * 因此每次进来都验一次;验不过就清掉,别让人拿着废令牌看空页面猜。
 */
import { useCallback, useEffect, useState } from 'react';
import { auth, hub, HubError } from '@/lib/hubClient';
import {
  passkeySupported, prepareCreationOptions, prepareRequestOptions,
  serializeAttestation, serializeAssertion, explainWebAuthnError,
} from '@/lib/webauthn';

export interface HubIdentity {
  /** 'admin' | 'device' —— 设备令牌也能登进来看,但管理操作会被 hub 403。 */
  kind: string;
  id: string;
}

export function useAuth() {
  const [user, setUser] = useState<HubIdentity | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  const verify = useCallback(async () => {
    if (!auth.signedIn) { setUser(null); setLoading(false); setInitialized(true); return null; }
    try {
      const me = await hub.whoami();
      const id: HubIdentity = { kind: me.kind, id: me.terminal || me.deviceId || 'unknown' };
      setUser(id);
      return id;
    } catch (e) {
      // 401 = 令牌不再有效, 清掉。其它错误(hub 不可达)也不放行:
      // 放行会让人以为登进来了, 然后每个页面各报各的错。
      if (e instanceof HubError && e.status === 401) auth.signOut();
      setUser(null);
      return null;
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  useEffect(() => { verify(); }, [verify]);

  /** 用一把令牌登录:先验再存,验不过不留。 */
  const signIn = useCallback(async (token: string) => {
    const prev = auth.token;
    auth.token = token.trim();
    try {
      const me = await hub.whoami();
      setUser({ kind: me.kind, id: me.terminal || me.deviceId || 'unknown' });
      return { ok: true as const };
    } catch (e) {
      auth.token = prev;
      const msg = e instanceof HubError
        ? (e.status === 401 ? '令牌无效:hub 不认这把令牌' : e.message)
        : 'hub 不可达:检查控制台的 /api 反代是否通';
      return { ok: false as const, error: msg };
    }
  }, []);

  /** passkey 登录 —— 不用输名字: 浏览器自己在钥匙串里挑, 人只按一下 Touch ID。 */
  const signInWithPasskey = useCallback(async () => {
    if (!passkeySupported()) {
      return { ok: false as const, error: '这个浏览器不支持 passkey, 或当前页面不是 https。' };
    }
    try {
      const begin = await hub.auth.loginBegin();
      const cred = await navigator.credentials.get({
        publicKey: prepareRequestOptions(begin.publicKey),
      }) as PublicKeyCredential | null;
      if (!cred) return { ok: false as const, error: '没有选择任何 passkey。' };
      const done = await hub.auth.loginFinish(begin.flowId, serializeAssertion(cred));
      auth.token = done.token;
      setUser({ kind: 'admin', id: `human:${done.name}` });
      return { ok: true as const };
    } catch (e) {
      // HubError 是服务端说的话(照原样给人看), 其余是浏览器抛的(要翻译)。
      if (e instanceof HubError) return { ok: false as const, error: e.message };
      return { ok: false as const, error: explainWebAuthnError(e) };
    }
  }, []);

  /** 用一次性注册码换一把 passkey。成功后直接就是登录态 —— 刚按过 Touch ID, 不该再登一次。 */
  const registerPasskey = useCallback(async (code: string, deviceName: string) => {
    if (!passkeySupported()) {
      return { ok: false as const, error: '这个浏览器不支持 passkey, 或当前页面不是 https。' };
    }
    try {
      const begin = await hub.auth.registerBegin(code.trim(), deviceName.trim());
      const cred = await navigator.credentials.create({
        publicKey: prepareCreationOptions(begin.publicKey),
      }) as PublicKeyCredential | null;
      if (!cred) return { ok: false as const, error: '注册被取消。' };
      const done = await hub.auth.registerFinish(begin.flowId, serializeAttestation(cred));
      auth.token = done.token;
      setUser({ kind: 'admin', id: `human:${done.name}` });
      return { ok: true as const };
    } catch (e) {
      if (e instanceof HubError) return { ok: false as const, error: e.message };
      return { ok: false as const, error: explainWebAuthnError(e) };
    }
  }, []);

  const signOut = useCallback(async () => {
    // 先告诉服务端把这把会话删掉, 再清本地。只清本地的话那把 token 还能用到
    // 30 天后过期 —— 在公用电脑上"退出登录"就成了个假动作。
    try { await hub.auth.logout(); } catch { /* 服务端不可达也要让人退出去 */ }
    auth.signOut();
    setUser(null);
  }, []);

  return {
    user, session: user, loading, initialized,
    signIn, signInWithPasskey, registerPasskey, signOut, refresh: verify,
  };
}
